import { useCallback, useEffect, useMemo, useRef } from 'react'
import { CellEditingStoppedEvent, ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { setAlertQueue } from 'cache/alerts'
import { defaultColDef } from 'components/AgGrid'
import { useUpdateDecisionEditPageLines } from 'hooks/use-update-decision-edit-page-lines'
import { CurrencyConvertLineItem, KeyValueResponse } from 'queries/useCurrencyConvertEditPage'
import { MessageType } from 'ts/enums/messageType.enums'
import {
  ProductDetailsLineItem,
  ProductsDetailsData,
} from 'ts/interfaces/productsDetails.interface'
import { useRefreshDataPointsCell } from '../../hooks/useRefreshDataPointsCell'
import type { ColumnKey } from '../../utils/dynamicColorMap'
import { applyDynamicColumnColor, buildDynamicHeaderStyles } from '../../utils/dynamicColorMap'
import { formatUpdateDecisionApiError } from '../../utils/formatUpdateDecisionApiError'
import type { ApprovalOption, ComparisonTab, GuidanceType } from '../ComparisonTabs'
import {
  createDataPointsCellRenderer,
  RowDataPoints,
} from '../DataPoint/createDataPointsCellRenderer'
import type { LinesFilterValue } from '../LinesFilter/LinesFilterDropdown'
import { LINES_FILTER } from '../LinesFilter/LinesFilterDropdown'
import { applyColumnVisibility } from '../shared/applyColumnVisibility'
import {
  parsePercentageEditValue,
  stringToNumber,
  validateBdNetValue,
  ValidationHandle,
} from '../validations'
import { frameworkComponents, productsDetailsGridOptions } from './agGridConfig/agGridConfig'
import { getProductsDetailsColDefs } from './agGridConfig/columnDefs'
import styles from './styles.module.scss'

interface ProductsDetailsGridProps {
  data: ProductsDetailsData | null
  currencyData?: CurrencyConvertLineItem[]
  isUSDSelected: boolean
  dealCurrency?: string
  activeTabs?: ComparisonTab[]
  selectedApprovalTypes?: string[]
  selectedGuidanceTypes?: GuidanceType[]
  colorMap?: Partial<Record<ColumnKey, string>>
  approvalOptions?: ApprovalOption[]
  dealId?: number
  linesFilter?: LinesFilterValue
  onRefresh?: () => Promise<void>
  onEditSuccess?: () => void
  modifiedLineNumbers?: Set<string>
}

enum CurrencyKVRKey {
  BdNet = 'bdNetPriceAmount1',
  BdNetUpdated = 'bdnetPriceAmountUpdated1',
}

type ConversionAmounts = {
  bdNet?: KeyValueResponse['amount']
  bdNetUpdated?: KeyValueResponse['amount']
}

const productsDetailsRowMapper = (row: ProductDetailsLineItem): RowDataPoints => ({
  baseline: row.dataPoints,
  ogmOrMargin: row.requestNetMarginPercentage,
  requestedValue: row.requestNetMarginPercentageDb ?? row.requestNetMarginPercentage,
  guidanceValues: {
    expertValue: row.calcGuidanceExpertPercentage,
    typicalValue: row.calcGuidanceTypicalPercentage,
    floorValue: row.calcGuidanceFloorPercentage,
    top25Value: row.historicalTop25DiscountPercentage,
    medianValue: row.historicalMedianDiscountPercentage,
    bottom25Value: row.historicalBottom25DiscountPercentage,
  },
  approvalValues: row.approvalData
    ? Object.fromEntries(
        Object.entries(row.approvalData).map(([id, detail]) => [id, detail.ogmPercent])
      )
    : {},
})

export const ProductsDetailsGrid = ({
  data,
  currencyData,
  isUSDSelected,
  dealCurrency,
  activeTabs,
  selectedApprovalTypes,
  selectedGuidanceTypes,
  colorMap,
  approvalOptions,
  dealId,
  linesFilter,
  onRefresh,
  onEditSuccess,
  modifiedLineNumbers,
}: ProductsDetailsGridProps) => {
  const gridRef = useRef<AgGridReact<ProductDetailsLineItem>>(null)
  const { updateDecisionEditPageLines } = useUpdateDecisionEditPageLines()

  const customDefaultColDef: ColDef = useMemo(
    () => ({
      ...defaultColDef,
      sortable: false,
      filter: false,
    }),
    []
  )

  const rowData = useMemo((): ProductDetailsLineItem[] => {
    if (!data) return []

    if (!(isUSDSelected && currencyData?.length)) {
      return data.lineItems
    }
    const conversionMap = new Map<string, ConversionAmounts>()

    for (const currItem of currencyData) {
      const conversion: ConversionAmounts = {}

      for (const kvr of currItem.keyValueResponses) {
        if (kvr.key === CurrencyKVRKey.BdNet) {
          conversion.bdNet = kvr.amount
        } else if (kvr.key === CurrencyKVRKey.BdNetUpdated) {
          conversion.bdNetUpdated = kvr.amount
        }
      }

      conversionMap.set(`${currItem.lineNumber}__${currItem.productNumber ?? ''}`, conversion)
    }
    return data.lineItems.map((item): ProductDetailsLineItem => {
      const conversion = conversionMap.get(`${item.id}__${item.productGenericNumberText ?? ''}`)

      const convertedBdNet = conversion?.bdNet ?? item?.requestBdNetPriceAmount
      const convertedBdNetUpdated =
        conversion?.bdNetUpdated ??
        item?.authBdnetPriceAmountUpdated ??
        item?.requestBdNetPriceAmountUpdated

      return {
        ...item,
        extendedRequestBdNet: convertedBdNet ?? item.extendedRequestBdNet,
        requestBdNetPriceAmountUpdated:
          item.requestBdNetPriceAmountUpdated != null ? convertedBdNetUpdated : null,
        authBdnetPriceAmountUpdated:
          item.authBdnetPriceAmountUpdated != null ? convertedBdNetUpdated : null,
        requestBdNetPriceAmount: item.requestBdNetPriceAmount != null ? convertedBdNet : null,
      }
    })
  }, [data, currencyData, isUSDSelected])

  const pinnedBottomRowData = useMemo(() => {
    if (linesFilter === LINES_FILTER.OPEN || linesFilter === LINES_FILTER.MY) return []
    if (!data?.totals) return []

    if (isUSDSelected && currencyData) {
      const { totalConvertedQuantity, totalConvertedBDNetAmount } = currencyData?.[0] ?? {}
      return [
        {
          id: 'totals',
          lineTypeCode: 'Total',
          estimatedQuantity: totalConvertedQuantity,
          bdNet: totalConvertedBDNetAmount,
        },
      ]
    }

    return [
      {
        id: 'totals',
        lineTypeCode: 'Total',
        estimatedQuantity: data.totals.qty,
        bdNet: data.totals.bdNet,
      },
    ]
  }, [data, isUSDSelected, currencyData, linesFilter])

  const containerClassName = useMemo(
    () => clsx('ag-theme-alpine', styles.productsDetailsGridContainer),
    []
  )

  useEffect(() => {
    if (isUSDSelected) {
      gridRef.current?.api?.stopEditing(true)
    }
    gridRef.current?.api?.refreshCells({ force: true })
  }, [isUSDSelected])

  const refreshDataPointsCell = useRefreshDataPointsCell<ProductDetailsLineItem>()

  const handleCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<ProductDetailsLineItem>) => {
      const editedValue = event.newValue ?? event.value

      if (!dealId || isUSDSelected || event.node?.rowPinned) {
        return
      }

      const field = event.colDef.field
      if (
        field !== 'extendedRequestBdNet' &&
        field !== 'requestNetMarginPercentage' &&
        field !== 'totalDiscountRequestPercentage'
      ) {
        return
      }

      const isRequestNetMarginField = field === 'requestNetMarginPercentage'
      const isTotalDiscountField = field === 'totalDiscountRequestPercentage'

      if (editedValue === event.oldValue) {
        return
      }

      const lineNumber = Number(event.data?.id)
      const plCode = event.data?.assosiatePlCode
      const isPercentageField = isRequestNetMarginField || isTotalDiscountField
      const parsedPercentageValue = isPercentageField ? parsePercentageEditValue(editedValue) : null

      if (isPercentageField && parsedPercentageValue === null) {
        if (event.node && typeof event.node.setDataValue === 'function') {
          event.node.setDataValue(field, event.oldValue)
        }
        refreshDataPointsCell(event)
        return
      }

      const parsedValue = isPercentageField ? parsedPercentageValue : stringToNumber(editedValue)

      // Prevent invalid values that are coerced to 0 from being saved
      // Only accept intentional 0 values (entered as '0' or 0)
      if (
        editedValue !== null &&
        editedValue !== undefined &&
        editedValue !== '' &&
        parsedValue === 0
      ) {
        const actualValue = typeof editedValue === 'string' ? editedValue.trim() : editedValue
        if (actualValue !== '0' && actualValue !== 0) {
          if (event.node && typeof event.node.setDataValue === 'function') {
            event.node.setDataValue(field, event.oldValue)
          }
          if (isRequestNetMarginField) {
            refreshDataPointsCell(event)
          }
          return // Invalid value that was coerced to 0 - reject the update
        }
      }

      if (!lineNumber || Number.isNaN(parsedValue) || !plCode) {
        if (isRequestNetMarginField) {
          refreshDataPointsCell(event)
        }
        return
      }

      const hasAuthBdNet = event.data?.authBdnetPriceAmountUpdated != null

      updateDecisionEditPageLines({
        variables: {
          updateRequest: {
            dealId,
            isPLTotalView: false,
            productUpdates: [
              {
                lineNumber,
                requestBdNetPriceAmount:
                  field === 'extendedRequestBdNet' && !hasAuthBdNet ? parsedValue : null,
                requestNetMarginPercentage:
                  isRequestNetMarginField && !hasAuthBdNet ? parsedValue : null,
                authBdNetPriceAmount:
                  field === 'extendedRequestBdNet' && hasAuthBdNet ? parsedValue : null,
                authNetMarginPercentage:
                  isRequestNetMarginField && hasAuthBdNet ? parsedValue : null,
                totalDiscountAuthPercentage: isTotalDiscountField ? parsedValue : undefined,
                plCode,
              },
            ],
          },
        },
      })
        .then((result) => {
          const updateResult = result.data?.updateDecisionEditPageLines
          const apiError = formatUpdateDecisionApiError(updateResult?.error)

          if (!updateResult?.isSuccess) {
            if (apiError) {
              setAlertQueue({
                type: MessageType.ERROR,
                message: apiError,
              })
            }
            if (event.node && field && typeof event.node.setDataValue === 'function') {
              event.node.setDataValue(field, event.oldValue)
            }
            if (isRequestNetMarginField) {
              refreshDataPointsCell(event)
            }
            return
          }

          // isSuccess === true but backend still returned a rule message; keep the value and show error.
          if (apiError) {
            setAlertQueue({
              type: MessageType.ERROR,
              message: apiError,
            })
          }
          if (isRequestNetMarginField) {
            refreshDataPointsCell(event)
          }
          onEditSuccess?.()

          if (onRefresh) {
            onRefresh().catch((error) => {
              console.error('Failed to refresh data after update:', error)
              setAlertQueue({
                type: MessageType.WARNING,
                message: 'Changes saved but failed to refresh. Please reload the page.',
              })
            })
          }
        })
        .catch((error) => {
          console.error('Failed to update decision edit page lines:', error)
          setAlertQueue({
            type: MessageType.ERROR,
            message: 'Failed to save changes. Please try again.',
          })
          if (event.node && field && typeof event.node.setDataValue === 'function') {
            event.node.setDataValue(field, event.oldValue)
          }
          if (isRequestNetMarginField) {
            refreshDataPointsCell(event)
          }
        })
    },
    [
      dealId,
      isUSDSelected,
      onRefresh,
      updateDecisionEditPageLines,
      onEditSuccess,
      refreshDataPointsCell,
    ]
  )

  const columnDefs = useMemo<ColDef<ProductDetailsLineItem>[]>(() => {
    const showRequestedPrice = activeTabs?.includes('requestedPrice') ?? false
    const guidanceSelections = selectedGuidanceTypes ?? []
    const showFloorGuidance = guidanceSelections.includes('floor')
    const showTypicalGuidance = guidanceSelections.includes('typical')
    const showExpertGuidance = guidanceSelections.includes('expert')
    const showTop25Guidance = guidanceSelections.includes('top25')
    const showMedianGuidance = guidanceSelections.includes('median')
    const showBottom25Guidance = guidanceSelections.includes('bottom25')

    const approvalPrefixHideMap: Partial<Record<string, boolean>> = {}
    approvalOptions?.forEach(({ id }) => {
      approvalPrefixHideMap[`approval-${id}-`] = !(selectedApprovalTypes?.includes(id) ?? false)
    })

    return getProductsDetailsColDefs(showRequestedPrice, approvalOptions).map((col) => {
      const withPrefixVisibility = applyColumnVisibility(
        col,
        {},
        {
          'requested-': !showRequestedPrice,
          ...approvalPrefixHideMap,
        }
      )
      if (withPrefixVisibility) {
        return colorMap
          ? applyDynamicColumnColor(withPrefixVisibility, colorMap)
          : withPrefixVisibility
      }

      if (!col.colId && col.field === 'extendedRequestBdNet') {
        return {
          ...col,
          cellEditor: 'agNumberCellEditor',
          valueParser: (params) => params.newValue,
          valueSetter: (params) => validateBdNetValue(params),
        }
      }

      if (!col.colId && col.field === 'requestNetMarginPercentage') {
        return {
          ...col,
          cellEditor: 'agNumberCellEditor',
          valueParser: (params) => Number(params.newValue),
          valueSetter: (params) => ValidationHandle(params),
        }
      }

      if (col.field === 'dataPoints') {
        return {
          ...col,
          cellRenderer: createDataPointsCellRenderer(
            productsDetailsRowMapper,
            showRequestedPrice,
            selectedApprovalTypes,
            guidanceSelections,
            colorMap
          ),
        }
      }

      const withFieldVisibility =
        applyColumnVisibility(
          col,
          {
            historicalBottom25DiscountPercentage: !showBottom25Guidance,
            historicalMedianDiscountPercentage: !showMedianGuidance,
            historicalTop25DiscountPercentage: !showTop25Guidance,
            calcGuidanceFloorPercentage: !showFloorGuidance,
            calcGuidanceTypicalPercentage: !showTypicalGuidance,
            calcGuidanceExpertPercentage: !showExpertGuidance,
          },
          {}
        ) ?? col

      return colorMap ? applyDynamicColumnColor(withFieldVisibility, colorMap) : withFieldVisibility
    })
  }, [activeTabs, selectedApprovalTypes, selectedGuidanceTypes, colorMap, approvalOptions])

  return (
    <section className={containerClassName} aria-label="Products details table">
      {colorMap && Object.keys(colorMap).length > 0 && (
        // eslint-disable-next-line react/no-danger
        <style dangerouslySetInnerHTML={{ __html: buildDynamicHeaderStyles(colorMap) }} />
      )}
      <AgGridReact<ProductDetailsLineItem>
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={customDefaultColDef}
        pinnedBottomRowData={pinnedBottomRowData}
        context={{ isUSDSelected, dealCurrency, modifiedLineNumbers }}
        onCellEditingStopped={handleCellEditingStopped}
        components={frameworkComponents}
        {...productsDetailsGridOptions}
      />
    </section>
  )
}
